import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Segmented } from "../styles";
import { colorFor, initials } from "../labels";

// ─── Avatar (círculo p/ usuários, squircle p/ projetos) ─────────────────────
const AvatarBox = styled.div`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  border-radius: ${({ $square, $size }) => ($square ? `${Math.max(7, Math.round($size * 0.26))}px` : "50%")};
  background: ${({ $color }) => `${$color}24`};
  border: 1px solid ${({ $color }) => `${$color}55`};
  /* no dark, clareia a cor da entidade para manter contraste das iniciais */
  color: ${({ $color, theme }) =>
    theme.body !== "#fafbfc" ? `color-mix(in srgb, ${$color} 55%, white)` : `color-mix(in srgb, ${$color} 85%, black)`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  font-size: ${({ $size }) => Math.round($size * 0.34)}px;
  font-weight: 600;
  letter-spacing: 0.02em;

  .online {
    position: absolute;
    right: ${({ $size }) => ($size > 40 ? 3 : 0)}px;
    bottom: ${({ $size }) => ($size > 40 ? 3 : 0)}px;
    width: ${({ $size }) => Math.max(8, Math.round($size * 0.24))}px;
    height: ${({ $size }) => Math.max(8, Math.round($size * 0.24))}px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 2px ${({ theme }) => (theme.body !== "#fafbfc" ? "#111318" : "#fff")}, 0 0 8px rgba(16,185,129,0.45);
  }
`;

export function Avatar({ id, name, color, size = 36, square = false, online = false, title }) {
  return (
    <AvatarBox $size={size} $square={square} $color={colorFor(id || name, color)} title={title || name}>
      {initials(name)}
      {online && <span className="online" />}
    </AvatarBox>
  );
}

// ─── Sparkline em SVG (leve, sem recharts) ──────────────────────────────────
export function Sparkline({ data = [], color = "#0a84ff", width = 96, height = 28, points = 30 }) {
  const values = data.slice(-points);
  if (values.length < 2) return <svg width={width} height={height} />;
  const max = Math.max(1, ...values);
  const step = width / (values.length - 1);
  const coords = values.map((v, i) => [i * step, height - 3 - (v / max) * (height - 6)]);
  const line = coords.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${line} L${width},${height} L0,${height} Z`;
  const gid = `sg-${color.replace("#", "")}`;
  const [lx, ly] = coords[coords.length - 1];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="2.4" fill={color} />
    </svg>
  );
}

// ─── Segmented control com "thumb" animado ─────────────────────────────────
export function SegmentedControl({ options, value, onChange, small, min }) {
  const ref = useRef(null);
  const [thumb, setThumb] = useState({ x: 0, w: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const btn = el.querySelector(`button[data-key="${value}"]`);
      if (btn) setThumb({ x: btn.offsetLeft - 2, w: btn.offsetWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value, options]);

  return (
    <Segmented ref={ref} $small={small} $min={min}>
      <span className="thumb" style={{ width: thumb.w, transform: `translateX(${thumb.x}px)` }} />
      {options.map((o) => (
        <button
          key={o.key}
          data-key={o.key}
          type="button"
          className={value === o.key ? "active" : ""}
          disabled={o.disabled}
          title={o.title}
          style={o.disabled ? { opacity: 0.3, cursor: "default" } : undefined}
          onClick={() => onChange(o.key)}
        >
          {o.icon}
          {o.label}
          {o.count !== undefined && <span className="count">{o.count}</span>}
        </button>
      ))}
    </Segmented>
  );
}
